import { useEffect, useRef } from "react";

/**
 * Fires `callback` every `intervalMs` while `enabled` is true AND the
 * browser tab is visible. Pauses automatically when the tab is hidden
 * and resumes (with an immediate tick) when the tab becomes visible
 * again — so the user doesn't burn bandwidth polling in a background
 * tab but gets fresh data the moment they come back.
 */
export const useAutoRefresh = (
  callback,
  { enabled, intervalMs = 30000, immediate = true } = {}
) => {
  const cbRef = useRef(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    let first = true;
    const start = () => {
      if (timer != null) return;
      const skip = first && !immediate;
      first = false;
      if (skip) {
        timer = setInterval(() => cbRef.current?.(), intervalMs);
        return;
      }
      // Fire immediately so the user doesn't wait a full interval for
      // the first refresh after enabling (or after returning to tab).
      cbRef.current?.();
      timer = setInterval(() => {
        cbRef.current?.();
      }, intervalMs);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, intervalMs, immediate]);
};
